import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CalendarCheck, Crown, Megaphone, ScanLine, Settings, Trash2 } from "lucide-react";
import { Btn, Empty, Modal, Page, Spinner, TopBar } from "@/components/kit";
import { useClearNotifications, useMarkAllRead, useNotifications, useOpenNotification } from "@/hooks/queries";
import { timeAgo } from "@/lib/toto";

const META = {
  reminders: { icon: CalendarCheck, fg: "#EA580C", bg: "#FFF7ED" },
  scanAlerts: { icon: ScanLine, fg: "#2563EB", bg: "#EFF6FF" },
  subscription: { icon: Crown, fg: "#7C3AED", bg: "#F5F3FF" },
  announcements: { icon: Megaphone, fg: "#059669", bg: "#ECFDF5" },
};
const ROUTES = { "/(tabs)": "/", "/(tabs)/contacts": "/contacts", "/(tabs)/events": "/events", "/subscription": "/subscription", "/notifications": "/notifications" };
const webRoute = (u) => {
  if (!u?.startsWith("/")) return null;
  if (ROUTES[u]) return ROUTES[u];
  if (u.startsWith("/contact/")) return u.replace("/contact/", "/contacts/");
  if (u.startsWith("/event/")) return u.replace("/event/", "/events/");
  return u.replace("/(tabs)", "") || "/";
};

export default function Notifications() {
  const nav = useNavigate();
  const list = useNotifications();
  const markAll = useMarkAllRead();
  const clear = useClearNotifications();
  const openN = useOpenNotification();
  const [confirm, setConfirm] = useState(false);
  const items = list.data ?? [];
  useEffect(() => {
    if (items.some((n) => !n.read)) markAll.mutate();
  }, [list.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (n) => {
    if (n.broadcastId) openN.mutate(n.id);
    const r = webRoute(n.actionUrl);
    if (r && !n.actionUrl.includes("/contact/x")) nav(r);
  };
  return (
    <Page>
      <TopBar title="Notifications" right={<>
        <Link to="/notifications/settings" className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F1F5F9]" data-testid="notifications-settings"><Settings className="w-5 h-5" /></Link>
        {items.length > 0 && <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#FEF2F2]" onClick={() => setConfirm(true)} data-testid="notifications-clear"><Trash2 className="w-5 h-5 text-[#DC2626]" /></button>}
      </>} />
      {list.isLoading && <Spinner />}
      <div className="space-y-2.5 mt-2 max-w-3xl" data-testid="notifications-list">
        {items.map((n) => {
          const m = META[n.category] ?? META.announcements;
          return (
            <button key={n.id} onClick={() => open(n)} className={`w-full text-left flex gap-3 p-4 rounded-[18px] border transition-colors ${n.read ? "bg-white border-[#E2E8F0]" : "bg-[#F5F5FF] border-[#E5E5FD]"} hover:border-[#0000EC]/30`} data-testid={`notification-${n.id}`}>
              <span className="w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: m.bg }}><m.icon className="w-5 h-5" style={{ color: m.fg }} /></span>
              <span className="flex-1 min-w-0">
                <span className="flex justify-between gap-2"><span className="text-[15px] font-bold">{n.title}</span><span className="text-[12px] text-[#64748B] shrink-0">{timeAgo(n.createdAt)}</span></span>
                <span className="block text-[14px] text-[#475569] mt-0.5">{n.message}</span>
              </span>
            </button>
          );
        })}
      </div>
      {list.isSuccess && !items.length && <Empty icon={Bell} title="You're all caught up" text="Reminders, scan alerts and announcements will appear here." testid="notifications-empty" />}
      <p className="mt-8 text-[12px] text-[#64748B] max-w-3xl">On the web, notifications appear in this in-app feed. Device push notifications are available in the TOTO mobile app.</p>
      <Modal open={confirm} onClose={() => setConfirm(false)} title="Clear all notifications?" testid="clear-notifications-modal">
        <div className="grid grid-cols-2 gap-2"><Btn variant="ghost" onClick={() => setConfirm(false)}>Cancel</Btn><Btn variant="danger" loading={clear.isPending} onClick={() => clear.mutate(undefined, { onSuccess: () => setConfirm(false) })} testid="clear-notifications-confirm">Clear</Btn></div>
      </Modal>
    </Page>
  );
}
